/* eslint-disable react/prop-types */
import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Card,
  Tabs,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  UserOutlined,
  InfoCircleOutlined,
  EnvironmentOutlined,
  SettingOutlined,
  ToolOutlined,
  DollarCircleOutlined,
  FileImageOutlined,
  UnorderedListOutlined,
  CreditCardFilled,
} from '@ant-design/icons';
import getQueryParams from 'utils/getQueryParams';
import FormSubmitControls from 'components/FormSubmitControls';
import Direccion from 'components/Direccion/index';
import LadaNumero from 'components/LadaNumero';
import Select from 'components/Select';
import FormSubmitCustomControls from 'components/FormSubmitCustomControls';
import AvatarUploader from 'components/AvatarUploader';
import { RFC_PATTERN } from 'utils/patterns';
import makeMessager from 'utils/complexFormsMessages';
import { makeGetOnFinish, makeOnChangeTabKey } from 'utils/tabsManagement';
import { onError } from 'utils/handlers';
import { toUpper } from 'utils/normalizers';
import { getModelPermission, isAdmin } from 'utils/permission/index';
import { getCatalogoDePaises, getCatalogoFolioRecibo, getCatalogoDePadrones } from 'api/catalogos';
import { getEntidad, postEntidad, patchEntidad } from 'api/catalogos/entidad';
import LlavesDePredios, { model as llavesModel, catalogsModels as llavesCatalogsModels } from 'components/Catalogos/LlavesDePredios';
import Variables, { permissionVariables } from 'components/Catalogos/Variables';
import { getConfiguracionesDeSegmentosDeFolios } from 'api/configuracion/configuracionesDeFolios';
import { getMetodosDePago } from 'api/configuracion';
import ActualizadorCarteras, { permissionCarteras } from 'components/Catalogos/ActualizadoprCarteras';
import Configurations, { entityConfigPermission } from './components/Configurations';
import ConfiguracionDeFolios, { model as foliosConfigModel, catalogsModels as foliosCatalogsModels } from './components/ConfiguracionDeFolios';
import CanalesDePago, { model as canalesModel, catalogsModels as canalesCatalogsModels } from './components/CanalesDePagoConfig';
import Cuentas, { model as cuentasModel } from './components/Cuentas';
import ConfigInfracciones, { model as infraccionesModel } from './components/Infracciones';
import Administrators from './components/Administrators';

const { TabPane } = Tabs;
const currentURI = '/catalogos/entidades/';

export const permissionEntidad = {
  permissionModel: 'entidad',
  catalogsModels: ['lada', 'catalogodepais'],
  tabsModels: [entityConfigPermission, permissionVariables, permissionCarteras],
};

const EntidadMunicipal = ({ permission }) => {
  const entityPermission = useMemo(() => getModelPermission('entidad'), []);
  const paisesPermission = useMemo(() => getModelPermission('catalogodepais'), []);
  const addressPermission = useMemo(() => getModelPermission('direccion'), []);
  const entidad = useSelector(({ auth }) => auth.entidad);
  const history = useHistory();
  const params = getQueryParams('tab', window.location.href);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [disabled, setDisabled] = useState(true);

  const [formAddress, setFormAddress] = useState();
  const [formConfigurations, setFormConfigurations] = useState();
  const [formConfigurationsFolio, setFormConfigurationsFolio] = useState();
  const [formConfigurationsInfraccion, setFormConfigurationsInfraccion] = useState();
  const [formActualizadorCarteras, setFormActualizadorCarteras] = useState();

  const [configurationPermission, setConfigurationPermission] = useState(null);
  const [llavesDePrediosPermission, setLlavesDePrediosPermission] = useState(null);
  const [foliosConfigPermission, setFoliosConfigPermission] = useState(null);
  const [canalesDePagoPermission, setCanalesDePagoPermission] = useState(null);
  const [cuentasPermission, setCuentasPermission] = useState(null);
  const [infraccionesPermission, setInfraccionesPermission] = useState(null);
  const [actCarterasPermission, setActCarterasPermission] = useState(null);
  const [folios, setFolios] = useState([]);
  const [foliosRecibos, setFoliosRecibos] = useState([]);
  const [padrones, setPadrones] = useState([]);
  const [metodosPago, setMetodosPago] = useState([]);
  const [paisesTemp, setPaisesTemp] = useState({ results: [] });

  const paises = useRef({ results: [] });

  const basicKeys = ['general', 'administradores'];
  const otherCatalogs = ['llavesDePredios', 'variables', 'canalesDePago', 'cuentas', 'carterasConfig'];
  const complexForms = [
    {
      key: 'direccion',
      formInstance: formAddress,
      permission: addressPermission,
    },
    {
      key: 'configurations',
      formInstance: formConfigurations,
      permission: configurationPermission,
    },
    {
      key: 'foliosConfig',
      formInstance: formConfigurationsFolio,
      permission: foliosConfigPermission,
    },
    {
      key: 'infraccionesConfig',
      formInstance: formConfigurationsInfraccion,
      permission: infraccionesPermission,
    },
    {
      key: 'carterasConfig',
      formInstance: formActualizadorCarteras,
      permission: actCarterasPermission,
    },
  ];

  const fetchData = async () => {
    setLoading(true);
    if (entityPermission?.view) {
      await getEntidad();
    }
    setLoading(false);
  };

  useEffect(() => {
    const getPais = async () => {
      if (entidad?.pais && paisesPermission?.view) {
        const _pais = await getCatalogoDePaises({ id: entidad.pais });
        setPaisesTemp(_pais);
      }
    };
    getPais();
    form.setFieldsValue(entidad);
  }, [entidad]);

  const onCancel = async () => {
    setDisabled(true);
    await fetchData();
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const success = await (values.id ? patchEntidad(values) : postEntidad(values));
      if (success) {
        setDisabled(true);
      }
      setLoading(false);
      return true;
    } catch (err) {
      onError(err, setLoading, [form], setCurrentTabKey);
      return false;
    }
  };

  useEffect(() => {
    let mounted = true;
    const permissionValidations = () => {
      const _configurationPermission = getModelPermission(
        entityConfigPermission.model,
        entityConfigPermission.catalogs,
      );
      const _llavesDePrediosPermission = getModelPermission(llavesModel, llavesCatalogsModels);
      const _foliosConfigPermission = getModelPermission(foliosConfigModel, foliosCatalogsModels);
      const _canalesDePagoPermission = getModelPermission(canalesModel, canalesCatalogsModels);
      const _cuentasPermission = getModelPermission(cuentasModel);
      const _infraccionesPermission = getModelPermission(infraccionesModel);
      const _actCarteraPermission = getModelPermission(
        permissionCarteras.model,
        permissionCarteras.catalogs,
      );

      if (mounted) {
        setConfigurationPermission(_configurationPermission);
        setLlavesDePrediosPermission(_llavesDePrediosPermission);
        setFoliosConfigPermission(_foliosConfigPermission);
        setCanalesDePagoPermission(_canalesDePagoPermission);
        setCuentasPermission(_cuentasPermission);
        setInfraccionesPermission(_infraccionesPermission);
        setActCarterasPermission(_actCarteraPermission);
      }
    };

    const fetchAll = async () => {
      setLoading(true);

      const promises = [
        getConfiguracionesDeSegmentosDeFolios(),
        getCatalogoFolioRecibo(),
        getMetodosDePago(),
        getCatalogoDePadrones(),
      ];

      if (isAdmin() && entityPermission?.view) {
        promises.push(getEntidad());
      }

      const [_folios, _foliosRecibos, _metodos, _padrones, _entidad] = await Promise.all(promises);
      if (mounted) {
        if (params) {
          setCurrentTabKey(params);
        }
        setFolios(_folios);
        setFoliosRecibos(_foliosRecibos);
        setMetodosPago(_metodos);
        setPadrones(_padrones);
        form.setFieldsValue(_entidad);
        setLoading(false);
      }
    };

    permissionValidations();
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const required = { required: true };

  const rules = {
    rfc: [
      required,
      {
        pattern: RFC_PATTERN,
        message: 'Ingrese un RFC válido',
      },
    ],
    required: [required],
    lada: [required],
  };

  const showMsg = makeMessager(setCurrentTabKey, fetchData);

  const onChangeTabKey = makeOnChangeTabKey(
    basicKeys, complexForms, currentTabKey, setCurrentTabKey, showMsg,
  );

  const getOnFinish = makeGetOnFinish(basicKeys, complexForms, currentTabKey, onFinish);
  const _onFinish = getOnFinish();

  const controls = [
    {
      onClick: () => history.push('/dashboard'),
      icon: <ArrowLeftOutlined />,
      text: 'Regresar',
    },
    (isAdmin()
      && (otherCatalogs.includes(currentTabKey)
        || (basicKeys.includes(currentTabKey) && permission.change)
        || !!complexForms.find((e) => e.key === currentTabKey)?.permission?.change)) && {
      onClick: () => setDisabled(false),
      icon: <EditOutlined />,
      text: 'Editar',
    },
  ].filter((e) => e);

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Card
          className="form-container"
          title="Entidad"
          extra={disabled ? (
            <FormSubmitCustomControls controls={controls} />
          ) : (
            <FormSubmitControls
              onFinish={() => _onFinish()}
              onCancel={onCancel}
              mutable={!disabled}
              allowSaveAndContinue
              onCancelText="Cancelar"
              permission={permission}
            />
          )}
          bordered={false}
        >
          <Tabs
            onChange={disabled ? setCurrentTabKey : onChangeTabKey}
            activeKey={currentTabKey}
            type="card"
          >
            <TabPane
              tab={(
                <span>
                  <InfoCircleOutlined />
                  Información General
                </span>
              )}
              key="general"
            >
              <Form
                layout="vertical"
                form={form}
                onFinish={() => onFinish()}
                scrollToFirstError
              >
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={8} lg={6}>
                    <AvatarUploader
                      disabled={disabled}
                      imageURL={form.getFieldValue('logotipo')}
                      formItemLabel="Logotipo oficial"
                      formItemName="logotipo"
                      form={form}
                      icon={<FileImageOutlined />}
                      height={256}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={16} lg={18}>
                    <Row gutter={10}>
                      <Col xs={24} sm={24} md={12} xl={8} xxl={8}>
                        <Form.Item
                          name="nombre"
                          rules={rules.required}
                          label="Nombre"
                        >
                          <Input allowClear disabled={disabled} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={12} xl={8} xxl={8}>
                        <Form.Item
                          name="RFC"
                          label="RFC"
                          rules={rules.rfc}
                          normalize={toUpper}
                        >
                          <Input
                            maxLength={12}
                            disabled={disabled}
                          />
                        </Form.Item>
                      </Col>
                      <LadaNumero
                        rules={rules}
                        form={form}
                        names={{
                          lada: 'lada',
                          numero_de_telefono: 'telefono',
                        }}
                        breakPoints={{
                          md: 24,
                          lg: 16,
                          xl: 8,
                          xxl: 8,
                        }}
                        disabled={disabled}
                        hasFeedback={!disabled}
                      />
                      <Col xs={24} sm={24} md={24} lg={16} xl={12} xxl={8}>
                        <Form.Item
                          name="razon_social"
                          label="Razón social"
                          rules={rules.required}
                        >
                          <Input allowClear disabled={disabled} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          label="Pais"
                          name="pais"
                          rules={rules.required}
                        >
                          <Select
                            dataSource={paisesTemp.results?.length ? paisesTemp : paises.current}
                            usingExternalDataSource={Boolean(paisesTemp.results?.length)}
                            labelProp="descripcion"
                            onClear={() => {
                              form.resetFields(['pais']); setPaisesTemp({ results: [] });
                            }}
                            pagination={{
                              getter: getCatalogoDePaises,
                              setter: (dataSource) => {
                                paises.current = dataSource;
                              },
                              config: { auto: true, qName: 'descripcion' },
                            }}
                            disabled={disabled}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Form.Item hidden name="id">
                  <Input allowClear />
                </Form.Item>
                <Form.Item hidden>
                  <Button htmlType="submit" />
                </Form.Item>
              </Form>
            </TabPane>
            {isAdmin() && (
              <TabPane
                tab={(
                  <span>
                    <UserOutlined />
                    Administradores
                  </span>
                )}
                key="administradores"
                forceRender
              >
                <Administrators
                  disabled={disabled}
                />
              </TabPane>
            )}
            {addressPermission?.view && (
              <TabPane
                tab={(
                  <span>
                    <EnvironmentOutlined />
                    Dirección
                  </span>
                )}
                key="direccion"
                forceRender
              >
                <Col span={24}>
                  <Direccion
                    useParentID={false}
                    currentURI={currentURI}
                    setLoading={setLoading}
                    setFormAddress={setFormAddress}
                    setCurrentTabKey={setCurrentTabKey}
                    callback={(values, _continue) => { if (!_continue) onCancel(); }}
                    mutable={!disabled}
                    permission={addressPermission}
                  />
                </Col>
              </TabPane>
            )}
            {configurationPermission?.view && (
              <TabPane
                tab={(
                  <span>
                    <SettingOutlined />
                    Configuraciones
                  </span>
                )}
                key="configurations"
                forceRender
              >
                <Configurations
                  setLoading={setLoading}
                  callback={(_continue) => {
                    if (_continue) {
                      onCancel();
                    }
                  }}
                  setCurrentTabKey={setCurrentTabKey}
                  setForm={setFormConfigurations}
                  disabled={disabled}
                  folios={folios}
                  foliosRecibos={foliosRecibos}
                  metodos={metodosPago}
                  padrones={padrones}
                />
              </TabPane>
            )}
            {/* {configurationPermission?.view && (
              <TabPane
                tab={(
                  <span>
                    <SettingOutlined />
                    Configuración de Infracciones
                  </span>
                )}
                key="configurations"
                forceRender
              >
                <ConfiguracionDeInfracciones
                  setLoading={setLoading}
                  callback={(_continue) => {
                    if (_continue) {
                      onCancel();
                    }
                  }}
                  setCurrentTabKey={setCurrentTabKey}
                  setForm={setFormConfigurations}
                  disabled={disabled}
                  folios={folios}
                  foliosRecibos={foliosRecibos}
                  metodos={metodosPago}
                  padrones={padrones}
                />
              </TabPane>
            )} */}
            {llavesDePrediosPermission?.view && (
              <TabPane
                tab={(
                  <span>
                    <SettingOutlined />
                    Llaves de Predios
                  </span>
                )}
                key="llavesDePredios"
                forceRender
              >
                <LlavesDePredios
                  setLoading={setLoading}
                  setCurrentTabKey={setCurrentTabKey}
                  disabled={disabled}
                  permission={llavesDePrediosPermission}
                />
              </TabPane>
            )}
            {permission.tabs[permissionVariables.model]?.view && (
              <TabPane
                tab={(
                  <span>
                    <SettingOutlined />
                    Variables
                  </span>
                )}
                key="variables"
                forceRender
              >
                <Variables
                  setLoading={setLoading}
                  setCurrentTabKey={setCurrentTabKey}
                  disabled={disabled}
                  permission={permission.tabs[permissionVariables.model]}
                />
              </TabPane>
            )}
            {foliosConfigPermission?.view && (
              <TabPane
                tab={(
                  <span>
                    <ToolOutlined />
                    Configuración de Folios
                  </span>
                )}
                key="foliosConfig"
                forceRender
              >
                <ConfiguracionDeFolios
                  setLoading={setLoading}
                  setForm={setFormConfigurationsFolio}
                  disabled={disabled}
                  setCurrentTabKey={setCurrentTabKey}
                  callback={async (_continue) => {
                    if (!_continue) {
                      onCancel();
                    }
                  }}
                />
              </TabPane>
            )}
            {canalesDePagoPermission?.view && (
              <TabPane
                tab={(
                  <span>
                    <DollarCircleOutlined />
                    Canales de Pago
                  </span>
                )}
                key="canalesDePago"
                forceRender
              >
                <CanalesDePago
                  setLoading={setLoading}
                  setCurrentTabKey={setCurrentTabKey}
                  disabled={disabled}
                  permission={canalesDePagoPermission}
                />
              </TabPane>
            )}
            {cuentasPermission?.view && (
              <TabPane
                tab={(
                  <span>
                    <UnorderedListOutlined />
                    Cuentas
                  </span>
                )}
                key="cuentas"
                forceRender
              >
                <Cuentas disabled={disabled} />
              </TabPane>
            )}
            { infraccionesPermission?.view && (
              <TabPane
                tab={(
                  <span>
                    <ToolOutlined />
                    Infracciones
                  </span>
                  )}
                key="infraccionesConfig"
                forceRender
              >
                <ConfigInfracciones
                  setLoading={setLoading}
                  setCurrentTabKey={setCurrentTabKey}
                  setForm={setFormConfigurationsInfraccion}
                  disabled={disabled}
                  callback={async (_continue) => {
                    if (!_continue) {
                      onCancel();
                    }
                  }}
                />
              </TabPane>
            )}
            {
            }
            { actCarterasPermission?.view && (
              <TabPane
                tab={(
                  <span>
                    <CreditCardFilled />
                    Actualizador de carteras
                  </span>
                  )}
                key="carterasConfig"
                forceRender
              >
                <ActualizadorCarteras
                  setLoading={setLoading}
                  setCurrentTabKey={setCurrentTabKey}
                  setForm={setFormActualizadorCarteras}
                  disabled={disabled}
                  permission={{
                    ...permission.tabs[permissionCarteras.model],
                  }}
                  callback={(_resp, _continue) => {
                    if (!_continue) {
                      onCancel();
                    }
                  }}
                />
              </TabPane>
            )}
          </Tabs>
        </Card>
      </Spin>
    </Row>
  );
};

export default EntidadMunicipal;
